/**
 * The JSON that goes inside an account QR code.
 *
 * Key ORDER matters here even though JSON objects are unordered: the string the customer
 * scans is what these functions produce, and keeping the shape identical to the agreed
 * spec makes the encoded text diffable against it byte for byte.
 *
 *   user     -> {"email":"...","name":"...","phone":"","type":"payment_request","for":"user","app":"amejpay","version":"1.0"}
 *   merchant -> {"email":"...","name":"...","phone":"","type":"merchant_payment","for":"corporate","app":"amejpay","version":"1.0"}
 */

import { cleanName, cleanText, resolvePhone, unwrapProfile } from "./format";

export const APP = "amejpay";
export const VERSION = "1.0";

/** "+977 9800000000" -> "+9779800000000". Empty stays empty. */
export function compactPhone(value) {
  const s = String(value || "").trim();
  if (!s) { return ""; }
  const digits = s.replace(/[^\d]/g, "");
  if (!digits) { return ""; }
  return (s.charAt(0) === "+" ? "+" : "") + digits;
}

function build(email, name, phone, type, audience) {
  return {
    email: String(email || "").trim(),
    name: cleanName(name) || cleanText(name),
    phone: compactPhone(phone),
    type: type,
    for: audience,
    app: APP,
    version: VERSION,
  };
}

/** Personal account: someone scans this to PAY the holder. */
export function userPayload(profile) {
  const p = unwrapProfile(profile);
  const a = p.author || p;
  return build(a.email || p.email, a.full_name || p.full_name, resolvePhone(p),
               "payment_request", "user");
}

/**
 * Merchant account. The business NAME comes from the merchant KYC record; merchant KYC
 * carries no email of its own, so the account email is used.
 */
export function merchantPayload(profile, kyc) {
  const p = unwrapProfile(profile);
  const a = p.author || p;
  const k = kyc || {};
  const name = k.business_name || a.company_name || k.name || a.full_name;
  const phone = k.contact_number || k.business_phone_number || resolvePhone(p);
  return build(a.email || p.email, name, phone, "merchant_payment", "corporate");
}

/** The exact string encoded into the QR. */
export function encode(payload) {
  return JSON.stringify(payload);
}

export default { APP, VERSION, compactPhone, userPayload, merchantPayload, encode };
